import React, { useEffect } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import IOSSwitch from "../common/Switch";
import { QuestionsList } from "../modals/chart-modal/QuestionsList";
import { useCrosstabQuestionSelection } from "@hooks/use-crosstab-question-selection";
import { useGetDatasetQuestionVariablesByElementQuery } from "@store/api/dataset-api";
import type {
  BreakoutConfig,
  BreakoutFocusMode,
} from "@store/slices/line-chart-config-slice";
import type { DatasetQuestionsSearchResponse } from "@/models/Dataset";
import type {
  QuestionVariable,
  QuestionValue,
  QuestionVariablesResponse,
} from "@/models/Filter";

type SourceLineItem = QuestionVariable | QuestionValue;

function isQuestionValue(item: SourceLineItem): item is QuestionValue {
  return !("vgroup" in item);
}

function getSourceLineId(item: SourceLineItem): string {
  return isQuestionValue(item) ? String(item.value) : item.label;
}

function getSourceLineOptions(
  data: QuestionVariablesResponse | undefined,
  questionType?: string,
  focusMode?: string,
): SourceLineItem[] {
  const variables = data?.variables ?? [];
  const values = data?.values ?? [];

  const isSingle = (questionType ?? "").toLowerCase() === "single";
  // Not single (Multiple, Float, Number) → always variables
  if (!isSingle && questionType) {
    return variables;
  }

  // Single + multi-var: focusMode decides which list
  if (focusMode) {
    return focusMode.toLowerCase() === "value" ? variables : values;
  }

  // Single + 1 var (no focusMode) → values
  return values;
}

interface LineChartBreakoutSectionProps {
  breakout: BreakoutConfig;
  datasetId: string | null;
  mainQuestionId: string | null;
  mainQuestionType?: string;
  mainFocusMode?: string;
  onToggle: (enabled: boolean) => void;
  onSourceLineChange: (value: { id: string; label: string } | null) => void;
  onBackfillSourceLineLabel: (id: string, label: string) => void;
  onQuestionChange: (question: DatasetQuestionsSearchResponse | null) => void;
  onFocusModeChange: (mode: BreakoutFocusMode | null) => void;
  onFocusVariableChange: (
    id: string | undefined,
    entityLabel: string | undefined,
  ) => void;
  onFocusValueChange: (
    id: string | undefined,
    entityValue: string | undefined,
  ) => void;
  showValidation?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

const LineChartBreakoutSection: React.FC<LineChartBreakoutSectionProps> = ({
  breakout,
  datasetId,
  mainQuestionId,
  mainQuestionType,
  mainFocusMode,
  onToggle,
  onSourceLineChange,
  onBackfillSourceLineLabel,
  onQuestionChange,
  onFocusModeChange,
  onFocusVariableChange,
  onFocusValueChange,
  showValidation = false,
  disabled = false,
  disabledReason,
}) => {
  const questionSelection = useCrosstabQuestionSelection({
    datasetId,
    questionType: "column",
  });

  // Fetch variables for the main question to populate Source Line dropdown.
  // Always fetch when datasetId and mainQuestionId are available (not gated on breakout.enabled)
  // so the data is cached and ready the moment the user enables breakout.
  const { data: mainQuestionVariables, isFetching: isFetchingVariables } =
    useGetDatasetQuestionVariablesByElementQuery(
      { id: datasetId ?? "", dataset_element_id: mainQuestionId ?? "" },
      { skip: !datasetId || !mainQuestionId },
    );
  const sourceLineOptions = getSourceLineOptions(
    mainQuestionVariables,
    mainQuestionType,
    mainFocusMode,
  );

  // Compute estimated breakout line count for "> 15 lines" warning
  const selectedBreakoutQuestion = questionSelection.questions.find(
    (q) => q.datasetElementId === breakout.questionId,
  );
  const estimatedLineCount = (() => {
    if (!selectedBreakoutQuestion) return 0;
    const type = (selectedBreakoutQuestion.type ?? "").toLowerCase();
    if (type === "multi") return selectedBreakoutQuestion.variablesCount;
    if (type === "single") {
      if (selectedBreakoutQuestion.variablesCount <= 1)
        return selectedBreakoutQuestion.valuesCount;
      if (breakout.focusMode === "VARIABLE")
        return selectedBreakoutQuestion.valuesCount;
      if (breakout.focusMode === "VALUE")
        return selectedBreakoutQuestion.variablesCount;
      return selectedBreakoutQuestion.valuesCount;
    }
    return 0;
  })();

  const isSingleMultiVariable =
    (
      breakout.questionType ??
      selectedBreakoutQuestion?.type ??
      ""
    ).toLowerCase() === "single" &&
    (breakout.variableCount ?? selectedBreakoutQuestion?.variablesCount ?? 0) >
      1;

  // Fetch variables/values for the breakout question (only when single multi-variable)
  const { data: breakoutQuestionVariables } =
    useGetDatasetQuestionVariablesByElementQuery(
      {
        id: datasetId ?? "",
        dataset_element_id: breakout.questionId ?? "",
      },
      {
        skip: !datasetId || !breakout.questionId || !isSingleMultiVariable,
      },
    );

  // Backfill sourceLineLabel for old charts that were saved without it
  useEffect(() => {
    if (
      breakout.enabled &&
      breakout.sourceLineId &&
      !breakout.sourceLineLabel &&
      sourceLineOptions.length > 0 &&
      !isFetchingVariables
    ) {
      const match = sourceLineOptions.find(
        (v) => getSourceLineId(v) === breakout.sourceLineId,
      );
      if (match) {
        const label =
          match.rowtitle || match.title || match.label || breakout.sourceLineId;
        onBackfillSourceLineLabel(breakout.sourceLineId, label);
      }
    }
  }, [
    breakout.enabled,
    breakout.sourceLineId,
    breakout.sourceLineLabel,
    sourceLineOptions,
    isFetchingVariables,
    onBackfillSourceLineLabel,
  ]);

  const sourceLineMissing =
    showValidation && breakout.enabled && !breakout.sourceLineId;
  const questionMissing =
    showValidation && breakout.enabled && !breakout.questionId;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Toggle row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Breakout
        </Typography>
        <IOSSwitch
          checked={breakout.enabled}
          onChange={(e) => onToggle(e.target.checked)}
          size="small"
          disabled={disabled}
        />
      </Box>

      {disabled && disabledReason && (
        <FormHelperText sx={{ mt: -1.5 }}>{disabledReason}</FormHelperText>
      )}

      {breakout.enabled && (
        <>
          {/* Source Line — populated from variables of the main question */}
          <FormControl fullWidth size="small" error={sourceLineMissing}>
            <FormLabel className="text-[12px] text-[#262626]">
              Source Line
            </FormLabel>
            <Select
              value={breakout.sourceLineId ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  onSourceLineChange(null);
                  return;
                }
                const v = (sourceLineOptions as SourceLineItem[]).find(
                  (v) => getSourceLineId(v) === val,
                );
                onSourceLineChange({
                  id: val,
                  label: v?.rowtitle || v?.title || v?.label || val,
                });
              }}
              displayEmpty
              disabled={disabled || isFetchingVariables}
              renderValue={(val) => {
                if (!val) {
                  return (
                    <Typography variant="body2" color="text.disabled">
                      {isFetchingVariables ? "Loading…" : "Select source line"}
                    </Typography>
                  );
                }
                const v = (sourceLineOptions as SourceLineItem[]).find(
                  (v) => getSourceLineId(v) === val,
                );
                return v?.rowtitle || v?.title || v?.label || val;
              }}
              endAdornment={
                isFetchingVariables ? (
                  <CircularProgress size={16} sx={{ mr: 2 }} />
                ) : undefined
              }
            >
              {(sourceLineOptions as SourceLineItem[]).map((v) => (
                <MenuItem key={v.datasetElementId} value={getSourceLineId(v)}>
                  {v.rowtitle || v.title || v.label}
                </MenuItem>
              ))}
            </Select>
            {sourceLineMissing && (
              <FormHelperText>Please select a source line.</FormHelperText>
            )}
          </FormControl>

          {/* Breakout Question */}
          <div>
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: "1px solid #eee",
                borderRadius: "8px !important",
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 40,
                  "& .MuiAccordionSummary-content": { my: 0.5 },
                }}
              >
                <FormLabel
                  className="text-[12px] text-[#262626]"
                  error={questionMissing}
                  sx={{ cursor: "pointer" }}
                >
                  Breakout Question
                </FormLabel>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search questions…"
                  value={questionSelection.searchTerm}
                  onChange={questionSelection.handleSearchChange}
                  disabled={disabled || !datasetId}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 0.5 }}
                />
                <QuestionsList
                  questions={questionSelection.questions}
                  selectedQuestionId={breakout.questionId ?? ""}
                  onQuestionSelect={(q) => onQuestionChange(q)}
                  searchTerm={questionSelection.searchTerm}
                  isFetching={questionSelection.isFetching}
                  hasMore={questionSelection.hasMore}
                  observerTarget={questionSelection.observerTarget}
                  compact={true}
                />
              </AccordionDetails>
            </Accordion>
            {questionMissing && (
              <FormHelperText error className="ml-[14px] mt-[4px]">
                Please select a breakout question.
              </FormHelperText>
            )}
          </div>

          {/* Focus mode — only for Single questions with multiple variables */}
          {isSingleMultiVariable && breakout.questionId && (
            <>
              <FormControl fullWidth size="small">
                <FormLabel className="text-[12px] text-[#262626]">
                  Focus Mode
                </FormLabel>
                <Select
                  value={breakout.focusMode ?? ""}
                  onChange={(e) =>
                    onFocusModeChange(
                      (e.target.value as BreakoutFocusMode) || null,
                    )
                  }
                  displayEmpty
                  disabled={disabled}
                  renderValue={(val) => {
                    if (!val) {
                      return (
                        <Typography variant="body2" color="text.disabled">
                          Select focus mode
                        </Typography>
                      );
                    }
                    return val === "VARIABLE"
                      ? "Variable Focus"
                      : "Value Focus";
                  }}
                >
                  <MenuItem value="VARIABLE">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Variable Focus
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pick one variable → 1 line per value
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="VALUE">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Value Focus
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pick one value → 1 line per variable
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {breakout.focusMode === "VARIABLE" && (
                <FormControl fullWidth size="small">
                  <FormLabel className="text-[12px] text-[#262626]">
                    Focus Variable
                  </FormLabel>
                  <Select
                    value={breakout.focusVariableId ?? ""}
                    onChange={(e) => {
                      const id = e.target.value || undefined;
                      const v = breakoutQuestionVariables?.variables?.find(
                        (v) => v.datasetElementId === e.target.value,
                      );
                      onFocusVariableChange(id, v?.label || v?.qlabel || id);
                    }}
                    displayEmpty
                    disabled={disabled}
                    renderValue={(val) => {
                      if (!val) {
                        return (
                          <Typography variant="body2" color="text.disabled">
                            Select variable
                          </Typography>
                        );
                      }
                      const v = breakoutQuestionVariables?.variables?.find(
                        (v) => v.datasetElementId === val,
                      );
                      return v?.rowtitle || v?.title || v?.label || val;
                    }}
                  >
                    {(breakoutQuestionVariables?.variables ?? []).map((v) => (
                      <MenuItem
                        key={v.datasetElementId}
                        value={v.datasetElementId}
                      >
                        {v.rowtitle || v.title || v.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {breakout.focusMode === "VALUE" && (
                <FormControl fullWidth size="small">
                  <FormLabel className="text-[12px] text-[#262626]">
                    Focus Value
                  </FormLabel>
                  <Select
                    value={breakout.focusValueId ?? ""}
                    onChange={(e) => {
                      const id = e.target.value || undefined;
                      const v = breakoutQuestionVariables?.values?.find(
                        (v) => v.datasetElementId === e.target.value,
                      );
                      const entityValue =
                        v?.value !== undefined ? String(v.value) : undefined;
                      onFocusValueChange(id, entityValue);
                    }}
                    displayEmpty
                    disabled={disabled}
                    renderValue={(val) => {
                      if (!val) {
                        return (
                          <Typography variant="body2" color="text.disabled">
                            Select value
                          </Typography>
                        );
                      }
                      const v = breakoutQuestionVariables?.values?.find(
                        (v) => v.datasetElementId === val,
                      );
                      return v?.title || v?.label || val;
                    }}
                  >
                    {(breakoutQuestionVariables?.values ?? []).map((v) => (
                      <MenuItem
                        key={v.datasetElementId}
                        value={v.datasetElementId}
                      >
                        {v.title || v.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {/* > 15 lines warning */}
          {estimatedLineCount > 15 && (
            <Alert
              severity="warning"
              sx={{
                fontSize: "0.75rem",
                "& .MuiAlert-message": { fontSize: "0.75rem" },
              }}
            >
              This breakout will create a large number of lines (
              {estimatedLineCount}) and may reduce readability and performance.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default LineChartBreakoutSection;
